import React from 'react';
import { Box, Card, GridItem, SkeletonLoader, CardProps } from '@drone-js/core';
import { DefaultRequestProps } from 'store/api/ApiConstants';
import { CardGridSize } from 'core-framework/constant/GridSize';
import { useSelector } from 'react-redux';
import { selectScreenSize } from 'store/slice/screen';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { CmsValue } from 'core/type/BootstrapConfig.types';

const CardPropsMobile: CardProps = {
    px: '16',
    py: '12',
    type: 'non-interactive',
    display: 'flex',
    alignItems: 'flex-start',
    borderRadius: '0',
    maxHeight: '376px',
};

const CardPropsDesktop: CardProps = {
    variant: 'elevation-s',
    type: 'interactive',
    display: 'block',
    borderRadius: '16',
    minWidth: '270px',
    me: '12',
};

const cardPropsBuilder = (isMobileScreen: boolean, forceDesktopLayout: boolean): CardProps => {
    if (forceDesktopLayout) return CardPropsDesktop;
    return isMobileScreen ? CardPropsMobile : CardPropsDesktop;
};

const ActivitySingleCardPlaceHolderMobile: React.FC<{ cms: CmsValue }> = ({ cms }) => {
    const loaderLabel = cms[326495];

    return (
        <Box display="flex" flexDirection="column" width="100%" ps="12" mx="16">
            <SkeletonLoader label={loaderLabel} shape="rectangle" width="100%" height="14px" mb="8" />
            <SkeletonLoader label={loaderLabel} shape="rectangle" width="50%" height="14px" mb="8" />
            <SkeletonLoader label={loaderLabel} shape="rectangle" width="75%" height="14px" />
            <Box display="flex" flexDirection="column" alignItems="flex-end" mt="48">
                <SkeletonLoader label={loaderLabel} shape="rectangle" width="45%" height="14px" mb="4" />
            </Box>
        </Box>
    );
};
ActivitySingleCardPlaceHolderMobile.displayName = 'ActivitySingleCardPlaceHolderMobile';

const ActivitySingleCardPlaceHolderDesktop: React.FC<{ cms: CmsValue }> = ({ cms }) => {
    const loaderLabel = cms[326495];

    return (
        <Box display="flex" flexDirection="column" pt="12" px="16" pb="16" height="184px">
            <SkeletonLoader label={loaderLabel} shape="rectangle" width="100%" height="14px" mb="8" />
            <SkeletonLoader label={loaderLabel} shape="rectangle" width="100%" height="14px" mb="8" />
            <SkeletonLoader label={loaderLabel} shape="rectangle" width="50%" height="14px" mb="8" />
            <SkeletonLoader label={loaderLabel} shape="rectangle" width="75%" height="14px" />
            <Box display="flex" flexDirection="column" alignItems="flex-end" mt="48">
                <SkeletonLoader label={loaderLabel} shape="rectangle" width="33%" height="14px" mb="4" />
            </Box>
        </Box>
    );
};
ActivitySingleCardPlaceHolderDesktop.displayName = 'ActivitySingleCardPlaceHolderDesktop';

export const ActivitySingleCardPlaceHolder: React.FC<{ responsive?: boolean; forceDesktopLayout?: boolean }> = React.memo((props) => {
    const screenSize = useSelector(selectScreenSize);
    const isResponsive = props.responsive ?? true; // Responsive is 'true' by default
    const useMobileLayout = isResponsive && screenSize.mobile;
    const elementName = useMobileLayout ? 'activities-single-placeholder-mobile' : 'activities-single-placeholder-desktop';

    const cms = useSelector(selectCmsFromBootstrap);

    return (
        <Card
            data-testid={elementName}
            status="default"
            overflow="hidden"
            height="100%"
            position="relative"
            {...cardPropsBuilder(useMobileLayout, props.forceDesktopLayout || false)}
        >
            <SkeletonLoader
                label={cms[326495]}
                shape="rectangle"
                width={useMobileLayout && !props.forceDesktopLayout ? '45%' : '100%'}
                height={useMobileLayout && !props.forceDesktopLayout ? '116px' : '195.1px'}
            />

            {useMobileLayout && !props.forceDesktopLayout ? <ActivitySingleCardPlaceHolderMobile cms={cms} /> : <ActivitySingleCardPlaceHolderDesktop cms={cms} />}
        </Card>
    );
});
ActivitySingleCardPlaceHolder.displayName = 'ActivitySingleCardPlaceHolder';

const ActivityCardPlaceHolder: React.FC<{ forceDesktopLayout?: boolean }> = React.memo((props) => {
    const numberOfCards = DefaultRequestProps.search.pagination.size;

    const placeHolderItems: React.JSX.Element[] = [];
    for (let i = 0; i < numberOfCards; i += 1) {
        placeHolderItems.push(
            <GridItem xl={CardGridSize.xl} lg={CardGridSize.lg} md={CardGridSize.md} sm={CardGridSize.sm} key={`placeholder-${i}`}>
                <ActivitySingleCardPlaceHolder forceDesktopLayout={props.forceDesktopLayout} />
            </GridItem>,
        );
    }

    return <>{placeHolderItems}</>;
});
ActivityCardPlaceHolder.displayName = 'ActivityCardPlaceHolder';

export default ActivityCardPlaceHolder;
